import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  Tab, Tabs, TextField, Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { CostDocument } from "../api/types";
import { parseMoney } from "../lib/money";
import { komunikatRejestru, useNipLookup } from "../lib/useNipLookup";
import { komunikatWykazu, useVatWhitelist } from "../lib/useVatWhitelist";

const PUSTY = {
  seller_nip: "", seller_name: "", number: "", issued_on: "",
  amount_net_control: "", amount_gross_control: "",
};

/**
 * Rejestracja kosztu — dwa warianty wejścia.
 *
 * Przy fakturze z KSeF kierownik podaje wyłącznie dane identyfikujące: dokument
 * żyje w KSeF i w GT, a kopia u nas byłaby trzecim miejscem do pilnowania.
 * Poza KSeF dokumentu nie ma nigdzie indziej, więc załącznik jest wymagany.
 */
export function RegisterCostDialog({
  open, onClose, projectCode,
}: { open: boolean; onClose: () => void; projectCode: string }) {
  const [wariant, setWariant] = useState<"ksef" | "scan">("ksef");
  const [form, setForm] = useState({ ...PUSTY });
  const [plik, setPlik] = useState<File | null>(null);
  const [blad, setBlad] = useState<ApiError | null>(null);
  const [zRejestru, setZRejestru] = useState("");
  const kolejka = useQueryClient();
  const nawiguj = useNavigate();
  const rejestr = useNipLookup(form.seller_nip);
  const wykaz = useVatWhitelist(form.seller_nip);

  // Nazwa sprzedawcy pokazana obok numeru jest najtańszym wyłapywaczem
  // literówki w całym module: pomylona cyfra daje poprawną sumę kontrolną
  // raz na jedenaście razy, a wtedy rejestr zwraca cudzą firmę i widać to
  // od razu — pod warunkiem, że nazwa jest na ekranie.
  useEffect(() => {
    const nazwa = rejestr.wynik?.company?.name ?? "";
    if (!nazwa) return;
    setForm((poprzedni) =>
      poprzedni.seller_name.trim() === "" || poprzedni.seller_name === zRejestru
        ? { ...poprzedni, seller_name: nazwa }
        : poprzedni,
    );
    setZRejestru(nazwa);
  }, [rejestr.wynik, zRejestru]);

  const pole = (name: keyof typeof PUSTY) => ({
    value: form[name],
    error: Boolean(blad?.field(name)),
    helperText: blad?.field(name),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [name]: e.target.value }),
  });

  const zapisz = useMutation({
    mutationFn: async () => {
      const wspolne = {
        source: wariant,
        seller_nip: form.seller_nip.replace(/[^0-9]/g, ""),
        seller_name: form.seller_name,
        number: form.number,
        issued_on: form.issued_on,
        amount_net_control: parseMoney(form.amount_net_control),
        amount_gross_control: parseMoney(form.amount_gross_control),
      };
      if (wariant === "ksef") {
        return api.post<CostDocument>(`/projects/${projectCode}/costs`, wspolne);
      }
      const dane = new FormData();
      Object.entries(wspolne).forEach(([k, v]) => dane.append(k, String(v)));
      if (plik) dane.append("file", plik);
      return api.postForm<CostDocument>(`/projects/${projectCode}/costs`, dane);
    },
    onSuccess: (koszt) => {
      kolejka.invalidateQueries({ queryKey: ["costs", projectCode] });
      setForm({ ...PUSTY });
      setPlik(null);
      setBlad(null);
      onClose();
      nawiguj(`/projekty/${projectCode}/koszty/${koszt.id}`);
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Zarejestruj koszt</DialogTitle>
      <DialogContent>
        <Tabs value={wariant} onChange={(_, v) => setWariant(v)} sx={{ mb: 2 }}>
          <Tab value="ksef" label="Faktura w KSeF" />
          <Tab value="scan" label="Paragon / wydruk / faktura zagraniczna" />
        </Tabs>

        <Stack spacing={2}>
          {blad && !Object.keys(blad.body.fields).length && (
            <Alert severity={blad.body.code === "duplicate" ? "warning" : "error"}>
              {blad.message}
            </Alert>
          )}

          {wariant === "ksef" ? (
            <Typography variant="body2" color="text.secondary">
              Dokumentu nie załączamy — żyje w KSeF i w GT. Podaj dane, po których
              go odnajdziemy, oraz kwotę z posiadanej kopii.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Ten dokument nie trafi do nas z KSeF, więc trzeba go załączyć.
              Sprawdź, czy paragon ma NIP nabywcy — jeśli nie, teraz jest ostatni
              moment, żeby poprosić o fakturę.
            </Typography>
          )}

          <Stack direction="row" spacing={2}>
            <TextField label="NIP sprzedawcy" {...pole("seller_nip")} sx={{ flex: 1 }}
                       error={Boolean(blad?.field("seller_nip")) || Boolean(rejestr.bladPola)}
                       helperText={blad?.field("seller_nip") ?? komunikatRejestru(rejestr)} />
            <TextField label="Nazwa sprzedawcy" {...pole("seller_name")} sx={{ flex: 2 }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Numer dokumentu" {...pole("number")} sx={{ flex: 2 }} />
            <TextField label="Data wystawienia" type="date" {...pole("issued_on")}
                       InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Netto" {...pole("amount_net_control")} sx={{ flex: 1 }}
                       helperText={blad?.field("amount_net_control")
                         ?? "Kwota z Twojej kopii — porównamy ją z tym, co przyjdzie z GT."} />
            <TextField label="Brutto" {...pole("amount_gross_control")} sx={{ flex: 1 }} />
          </Stack>

          {rejestr.wynik?.warnings.map((uwaga) => (
            <Alert key={uwaga} severity="warning">{uwaga}</Alert>
          ))}

          {/* Status w wykazie VAT rozstrzyga o tym, czy z tej faktury jest co
              odliczyć. Miejsce na tę informację jest tutaj, przy wpisywaniu
              dokumentu — przy zamknięciu miesiąca jest już tylko problemem. */}
          {komunikatWykazu(wykaz) && (
            <Alert severity="warning">{komunikatWykazu(wykaz)}</Alert>
          )}

          {wariant === "scan" && (
            <Button component="label" variant="outlined">
              {plik ? plik.name : "Wybierz plik"}
              <input hidden type="file" accept="image/*,application/pdf"
                     onChange={(e) => setPlik(e.target.files?.[0] ?? null)} />
            </Button>
          )}
          {blad?.field("file") && <Alert severity="error">{blad.field("file")}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={() => zapisz.mutate()}
                disabled={zapisz.isPending}>
          Zarejestruj
        </Button>
      </DialogActions>
    </Dialog>
  );
}
